/**
 * Preferencias de voz/orb persistidas en localStorage. Aisladas en su propio
 * módulo para que los hooks (`useCaptureMode`, `useTtsVolume`) y el side-channel
 * (radial menu, gestos) compartan la misma source of truth sin re-leer keys
 * desde varios componentes.
 *
 * Eventos sintéticos `orborix:pref.<key>` se disparan en `window` cada vez
 * que un setter persiste un valor — los hooks suscriptos los reciben para
 * mantener su estado React sincronizado entre tabs/componentes.
 */

export type CaptureMode = 'toggle' | 'hold' | 'free';

export const CAPTURE_MODE_KEY = 'orborix.captureMode';
export const TTS_VOLUME_KEY = 'orborix.ttsVolume';
export const SFX_MUTED_KEY = 'orborix.sfxMuted';
export const SFX_VOLUME_KEY = 'orborix.sfxVolume';
const SPEECH_RATE_KEY_PREFIX = 'orborix.speechRate.';
const SPEECH_RATE_KEY_GLOBAL = 'orborix.speechRate';

export const DEFAULT_CAPTURE_MODE: CaptureMode = 'toggle';
export const DEFAULT_TTS_VOLUME = 1;
/** Sonidos de UI ENCENDIDOS por defecto (no muteados). */
export const DEFAULT_SFX_MUTED = false;
export const DEFAULT_SFX_VOLUME = 0.8;
export const DEFAULT_SPEECH_RATE = 1;

/**
 * Velocidades soportadas por el assistant. Son los valores REALES que viajan
 * al provider TTS (ElevenLabs Flash v2.5: rango oficial 0.7..1.2 con pitch
 * preservado nativo). La UI muestra labels "amigables" (0.5x/1x/1.5x)
 * mapeados por `SPEECH_RATE_LABELS`.
 *
 * Aplicado backend-side via `voice_settings.speed` por context — el cambio
 * toma efecto al PRÓXIMO turno (ElevenLabs no permite mutar voice_settings
 * mid-context). El cliente lo persiste por módulo en localStorage para que
 * el próximo handshake mande `?speechRate=` y el primer turno ya use la
 * pref.
 */
export const SPEECH_RATE_VALUES: ReadonlyArray<number> = [0.7, 0.85, 1.0, 1.1, 1.2];
const SPEECH_RATE_MIN = 0.7;
const SPEECH_RATE_MAX = 1.2;

/**
 * Label "amigable" que mostramos en la UI por cada valor real del provider.
 * Pensado para que el usuario perciba un rango más amplio que el real —
 * los labels `0.5x / 0.75x / 1x / 1.25x / 1.5x` son los conocidos de
 * cualquier reproductor. Los valores reales viven en el rango oficial de
 * ElevenLabs (0.7..1.2) donde el pitch queda preservado nativo.
 */
export const SPEECH_RATE_LABELS: Readonly<Record<string, string>> = {
  '0.7': '0.5x',
  '0.85': '0.75x',
  '1': '1x',
  '1.1': '1.25x',
  '1.2': '1.5x',
};

/**
 * Mapea un valor real (0.7/1/1.2) a su label de UI. Cae al formato genérico
 * si el valor no está en la tabla (defensivo, no debería pasar dado el
 * clamp).
 */
export function formatSpeechRateLabel(rate: number): string {
  const key = rate === 1 ? '1' : String(rate);
  return SPEECH_RATE_LABELS[key] ?? `${rate}x`;
}

const CAPTURE_MODES: ReadonlyArray<CaptureMode> = ['toggle', 'hold', 'free'];

function isCaptureMode(v: unknown): v is CaptureMode {
  return typeof v === 'string' && (CAPTURE_MODES as ReadonlyArray<string>).includes(v);
}

export function readCaptureMode(): CaptureMode {
  if (typeof window === 'undefined') return DEFAULT_CAPTURE_MODE;
  const raw = window.localStorage.getItem(CAPTURE_MODE_KEY);
  return isCaptureMode(raw) ? raw : DEFAULT_CAPTURE_MODE;
}

export function writeCaptureMode(mode: CaptureMode): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CAPTURE_MODE_KEY, mode);
  window.dispatchEvent(new CustomEvent('orborix:pref.captureMode', { detail: mode }));
}

export function readTtsVolume(): number {
  if (typeof window === 'undefined') return DEFAULT_TTS_VOLUME;
  const raw = window.localStorage.getItem(TTS_VOLUME_KEY);
  if (raw == null) return DEFAULT_TTS_VOLUME;
  const n = Number(raw);
  if (!Number.isFinite(n)) return DEFAULT_TTS_VOLUME;
  return Math.min(1, Math.max(0, n));
}

export function writeTtsVolume(volume: number): void {
  if (typeof window === 'undefined') return;
  const clamped = Math.min(1, Math.max(0, volume));
  window.localStorage.setItem(TTS_VOLUME_KEY, String(clamped));
  window.dispatchEvent(new CustomEvent('orborix:pref.ttsVolume', { detail: clamped }));
}

/**
 * Mute de los SFX de UI (sonidos sintetizados de speak). Device-local, NO en la
 * DB — mismo criterio que el volumen de TTS (un usuario silencia los efectos en
 * su teléfono, no en su cuenta). Independiente del mute/volumen de la voz del
 * coach. Lo consume el motor de SFX (`apps/speak/src/lib/sfx/engine.ts`) y el
 * hook `useSfxMuted` para el toggle de UI.
 */
export function readSfxMuted(): boolean {
  if (typeof window === 'undefined') return DEFAULT_SFX_MUTED;
  return window.localStorage.getItem(SFX_MUTED_KEY) === '1';
}

export function writeSfxMuted(muted: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SFX_MUTED_KEY, muted ? '1' : '0');
  window.dispatchEvent(new CustomEvent('orborix:pref.sfxMuted', { detail: muted }));
}

/** Volumen de los SFX de UI (0..1). Device-local, independiente del TTS. */
export function readSfxVolume(): number {
  if (typeof window === 'undefined') return DEFAULT_SFX_VOLUME;
  const raw = window.localStorage.getItem(SFX_VOLUME_KEY);
  if (raw == null) return DEFAULT_SFX_VOLUME;
  const n = Number(raw);
  if (!Number.isFinite(n)) return DEFAULT_SFX_VOLUME;
  return Math.min(1, Math.max(0, n));
}

export function writeSfxVolume(volume: number): void {
  if (typeof window === 'undefined') return;
  const clamped = Math.min(1, Math.max(0, volume));
  window.localStorage.setItem(SFX_VOLUME_KEY, String(clamped));
  window.dispatchEvent(new CustomEvent('orborix:pref.sfxVolume', { detail: clamped }));
}

/**
 * Velocidad de habla del assistant, persistida por módulo (`slug`). Si el slug
 * es `null` (Home sin módulo activo) usa la key global. Override por módulo
 * permite, por ejemplo, "lento" en `english` para principiantes y "normal" en
 * `coach`. Aplicada client-side via `AudioPlayer.setPlaybackRate` — el
 * karaoke reescala sus timings con el mismo factor.
 */
function speechRateKey(slug: string | null): string {
  return slug ? `${SPEECH_RATE_KEY_PREFIX}${slug}` : SPEECH_RATE_KEY_GLOBAL;
}

function clampSpeechRate(rate: number): number {
  if (!Number.isFinite(rate)) return DEFAULT_SPEECH_RATE;
  return Math.min(SPEECH_RATE_MAX, Math.max(SPEECH_RATE_MIN, rate));
}

export function readSpeechRate(slug: string | null): number {
  if (typeof window === 'undefined') return DEFAULT_SPEECH_RATE;
  const raw = window.localStorage.getItem(speechRateKey(slug));
  if (raw == null) return DEFAULT_SPEECH_RATE;
  const n = Number(raw);
  return Number.isFinite(n) ? clampSpeechRate(n) : DEFAULT_SPEECH_RATE;
}

export function writeSpeechRate(slug: string | null, rate: number): void {
  if (typeof window === 'undefined') return;
  const clamped = clampSpeechRate(rate);
  window.localStorage.setItem(speechRateKey(slug), String(clamped));
  window.dispatchEvent(
    new CustomEvent('orborix:pref.speechRate', { detail: { slug, rate: clamped } }),
  );
}
